import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { getUserEmail } from '@/utils/storage';
import {
  ApiEnvelope,
  CreatePackageRequest,
  GetPackagesParams,
  GetPackageVehiclesParams,
  Paginated,
  PackageDetail,
  PackageHeldBy,
  PackageHistoryEntry,
  PackageListItem,
  PackageStock,
  PackageStockPreview,
  PackageStockPreviewRequest,
  PackageVehicles,
  PackageVehiclesPreviewRequest,
  PackageWriteBody,
  UpdatePackageRequest,
} from '@/types/package';

// Dash Rent management surface: /<product>/<actor>/<version>/<resource>.
const BASE = '/rent/mgmt/v1/packages';

/**
 * Dash Rent wraps every response in `{ status, message, data }`, unlike the
 * older FMS endpoints that return the payload at the top level — so the
 * payload here is one level deeper.
 */
const unwrap = <T>(payload: ApiEnvelope<T>): T => payload?.data as T;

/**
 * Some endpoints return the primary key as uppercase `ID` (Drizzle PK casing)
 * even though the contract documents `id` — the same mismatch PurchaseOrder
 * already works around. Normalising here means callers only ever see `id`,
 * instead of silently building a `/packages/undefined` URL.
 */
const withId = <T extends { id?: string; ID?: string }>(row: T): T =>
  row && !row.id && row.ID ? { ...row, id: row.ID } : row;

/**
 * Fails fast on a missing or literal "undefined" path param, so a bad URL
 * surfaces as a readable message instead of a uuid parse error from Postgres.
 */
const requireId = (id: string | undefined): string => {
  if (!id || id === 'undefined' || id === 'null') {
    throw new Error('Package id is missing from the URL');
  }
  return id;
};

export const getPackages = async (
  params: GetPackagesParams = {}
): Promise<Paginated<PackageListItem>> => {
  try {
    const response =
      await Axios.get<ApiEnvelope<Paginated<PackageListItem>>>(BASE, {
        params,
      });
    const page = unwrap(response.data);
    return { ...page, items: (page?.items ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getPackageById = async (id: string): Promise<PackageDetail> => {
  try {
    const response = await Axios.get<ApiEnvelope<PackageDetail>>(
      `${BASE}/${requireId(id)}`
    );
    const detail = withId(unwrap(response.data));
    return detail && { ...detail, rules: (detail.rules ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** On-demand availability count; evaluated at request time (TRD P5). */
export const getPackageStock = async (id: string): Promise<PackageStock> => {
  try {
    const response = await Axios.get<ApiEnvelope<PackageStock>>(
      `${BASE}/${requireId(id)}/stock`
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Every unit of the package's model with whether it is stock and, if not, why.
 * `summary` covers the whole model; search and `available` only narrow `items`.
 */
export const getPackageVehicles = async (
  id: string,
  params: GetPackageVehiclesParams
): Promise<PackageVehicles> => {
  try {
    const response = await Axios.get<ApiEnvelope<PackageVehicles>>(
      `${BASE}/${requireId(id)}/vehicles`,
      { params }
    );
    const page = unwrap(response.data);
    return { ...page, items: (page?.items ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * `getPackageVehicles` for criteria that only exist in the form (create, or an
 * edit in progress): the units behind `getPackageStockPreview`'s count.
 */
export const getPackageVehiclesPreview = async (
  body: PackageVehiclesPreviewRequest
): Promise<PackageVehicles> => {
  try {
    const response = await Axios.post<ApiEnvelope<PackageVehicles>>(
      `${BASE}/stock-preview/vehicles`,
      body
    );
    const page = unwrap(response.data);
    return { ...page, items: (page?.items ?? []).map(withId) };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Same count as `getPackageStock`, but for criteria that only exist in the
 * form — it takes the model plus the compiled criteria instead of a package
 * id, so the Condition editor can show the blast radius before saving.
 *
 * POST rather than GET because the criteria object is nested; nothing is
 * written.
 */
export const getPackageStockPreview = async (
  body: PackageStockPreviewRequest
): Promise<PackageStockPreview> => {
  try {
    const response = await Axios.post<ApiEnvelope<PackageStockPreview>>(
      `${BASE}/stock-preview`,
      body
    );
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * The API requires the acting user even though the contract omits it from the
 * documented body, so it is stamped here rather than at each call site — that
 * way no caller can forget it and trip "createdBy must be a string".
 */
export const createPackage = async (
  body: PackageWriteBody
): Promise<PackageDetail> => {
  try {
    const payload: CreatePackageRequest = {
      ...body,
      createdBy: getUserEmail(),
    };
    const response = await Axios.post<ApiEnvelope<PackageDetail>>(
      BASE,
      payload
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** PUT, not PATCH — rules are inline and replace-all on update. */
export const updatePackage = async (
  id: string,
  body: PackageWriteBody
): Promise<PackageDetail> => {
  try {
    const payload: UpdatePackageRequest = {
      ...body,
      updatedBy: getUserEmail(),
    };
    const response = await Axios.put<ApiEnvelope<PackageDetail>>(
      `${BASE}/${requireId(id)}`,
      payload
    );
    return withId(unwrap(response.data));
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// A plain Error would flatten the holders into "PACKAGE_IN_USE".
export class PackageInUseError extends Error {
  constructor(public heldBy: PackageHeldBy[]) {
    super('PACKAGE_IN_USE');
    this.name = 'PackageInUseError';
  }
}

/**
 * Flips status to ARCHIVED so history stays intact. Archiving stops future
 * matching; rentals already running are protected by their own terms
 * snapshot.
 *
 * Soft-deletes through `DELETE /packages/:ID`, which is what the controller
 * actually exposes — there is no `POST /:id/archive`. It takes a body: the
 * actor is required, and an optional `reason` is written into the history row
 * alongside the before/after snapshots, so passing one makes the audit trail
 * worth reading.
 */
export const archivePackage = async (
  id: string,
  reason?: string
): Promise<void> => {
  try {
    await Axios.delete(`${BASE}/${requireId(id)}`, {
      data: { updatedBy: getUserEmail(), ...(reason ? { reason } : {}) },
    });
  } catch (error: unknown) {
    // api-contract §12: `409 { error: 'PACKAGE_IN_USE', data: { heldBy } }`.
    const res = (error as any)?.response;
    const heldBy =
      res?.data?.error === 'PACKAGE_IN_USE' ? res.data.data?.heldBy : undefined;
    if (res?.status === 409 && Array.isArray(heldBy)) {
      throw new PackageInUseError(heldBy);
    }
    // Fleet fails closed when On Demand is down; the bare code helps no one.
    if (getBackendErrorMessage(error) === 'COUPON_UNAVAILABLE') {
      throw new Error(
        'Pemakaian package tidak bisa dicek karena On Demand tidak menjawab. Coba lagi nanti.'
      );
    }
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Reverses archive. The package lands INACTIVE, not ACTIVE, so ops reviews its
 * terms and switches it on through the edit form; 409 when another package took
 * the name meanwhile.
 */
export const unarchivePackage = async (id: string): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${requireId(id)}/unarchive`, {
      updatedBy: getUserEmail(),
    });
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** Before/after snapshots with a reason, newest first. */
export const getPackageHistory = async (
  id: string,
  params: { page?: number; size?: number } = {}
): Promise<Paginated<PackageHistoryEntry>> => {
  try {
    const response = await Axios.get<
      ApiEnvelope<Paginated<PackageHistoryEntry>>
    >(`${BASE}/${requireId(id)}/history`, { params });
    return unwrap(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
